import { AuthService } from '@services/AuthService';
import { AuthSlice } from '@/store/reducers/AuthReducer';
import { AppDispatch } from '@/store/index';
import { UserService } from '@services/UserService';
import { TwoFactorAuthenticationService } from '@services/TwoFactorAuthenticationService';
import { SamlAuthService } from '@services/SamlAuthService';
import { UserAvatarSlice } from '@/store/reducers/UserAvatarReducer';
import { auth } from '@/auth';
import { applySignInSettings } from '@/auth/signInSettings';
import { closeSupportSession } from '@/auth/supportSessionClient';
import { errorTextWithTraceId } from '@srm/core/errors';
import { IAuthResponse } from '@models/response/IAuthResponse';

/**
 * Применить успешный ответ входа: сессия (фактический режим ответа → токены → таймер ротации) — в `auth`, профиль —
 * в стор. Токены в стор не попадают: их видно в DevTools и снимках состояния.
 */
const applyAuthResponse = (data: IAuthResponse, dispatch: AppDispatch) => {
    auth.acceptLogin(data);
    dispatch(AuthSlice.actions.successLogin({ user: data.user }));
};

export const AuthActionCreators = {
    /**
     * Изолированный support preview не имеет права вызывать обычные API (`/User`,
     * `/Account/SignInSettings`): request guard пропускает только session-scoped endpoints.
     * Для его route guard достаточно access-токена, уже восстановленного startAuth из
     * storage/httpOnly refresh-cookie (он же поставил таймер ротации). Отмечаем токен в Redux без загрузки профиля.
     */
    initializeSupportPreview: () => (dispatch: AppDispatch) => {
        dispatch(AuthSlice.actions.accessTokenChanged(!!auth.getAccessToken()));
        dispatch(AuthSlice.actions.setIsLoading(false));
    },

    login: (username: string, password: string, remember: boolean) => async (dispatch: AppDispatch) => {
        try {
            dispatch(AuthSlice.actions.setIsLoggingIn(true));

            const deviceId = auth.getDeviceId();
            const requestedMode = auth.getMode();
            const response = await AuthService.login(username, password, remember, deviceId, requestedMode);

            applyAuthResponse(response.data, dispatch);
        } catch (e) {
            console.log(e);
            // На экране входа код обращения больше некуда положить: тост интерцептора здесь
            // не показывается, а человек уже никуда не может уйти, кроме поддержки.
            const error = errorTextWithTraceId(e, 'Не удалось выполнить вход. Повторите попытку.');
            dispatch(AuthSlice.actions.setError({ message: error, login: username, loginType: 'LoginPass' }));
        } finally {
            dispatch(AuthSlice.actions.setIsLoggingIn(false));
        }
    },

    loginByHeader: (rememberMe: boolean) => async (dispatch: AppDispatch) => {
        try {
            dispatch(AuthSlice.actions.setIsLoggingIn(true));

            const response = await AuthService.loginByHeader(rememberMe);

            if (!response) {
                throw 'Не удалось выполнить вход через SSO';
            }

            applyAuthResponse(response.data, dispatch);
        } catch (e) {
            console.log(e);
            const error = errorTextWithTraceId(e, 'Не удалось выполнить вход через SSO. Повторите попытку.');
            dispatch(AuthSlice.actions.setError({ message: error, loginType: 'SSO' }));
        } finally {
            dispatch(AuthSlice.actions.setIsLoggingIn(false));
        }
    },

    loginBySaml: () => async (dispatch: AppDispatch) => {
        try {
            dispatch(AuthSlice.actions.setIsLoggingIn(true));

            const response = await SamlAuthService.loginAsync();

            if (!response) {
                throw 'Не удалось выполнить вход через ADFS';
            }

            applyAuthResponse(response.data, dispatch);
        } catch (e) {
            console.log(e);
            const error = errorTextWithTraceId(e, 'Не удалось выполнить вход через ADFS. Повторите попытку.');
            dispatch(AuthSlice.actions.setError({ message: error, loginType: 'SAML' }));
        } finally {
            dispatch(AuthSlice.actions.setIsLoggingIn(false));
        }
    },

    logout: () => async (dispatch: AppDispatch) => {
        try {
            dispatch(AuthSlice.actions.setIsLoading(true));
            // Вкладка «от имени» закрывает свой сеанс поддержки, обычная — SignOut. Токены сняты в любом случае.
            await (auth.isIsolated() ? closeSupportSession() : auth.signOut());
        } catch (e) {
            console.log(e);
        } finally {
            // Выход и при сбое SignOut (сеть, 5xx) — иначе LoginForm увидит isAuth и вернёт на прежнюю страницу.
            // Навигация на /login — ответственность компонента, вызывающего logout
            // (Navbar): он уводит на чистый /login ДО dispatch, чтобы
            // ProtectedRoute не подмешивал ?returnUrl= из текущего URL.
            dispatch(AuthSlice.actions.successLogout());
            dispatch(UserAvatarSlice.actions.clearAvatar());
            dispatch(AuthSlice.actions.setIsLoading(false));
        }
    },

    /** `revoked: false` — запрос выхода через IdP не дошёл: семейство refresh на сервере живо (см. `clearSession`). */
    logoutBySaml: (revoked = true) => async (dispatch: AppDispatch) => {
        try {
            dispatch(AuthSlice.actions.setIsLoading(true));
            auth.clearSession({ revoked });
            dispatch(AuthSlice.actions.successLogout());
            dispatch(UserAvatarSlice.actions.clearAvatar());
        } catch (e) {
            console.log(e);
        }
    },

    getCurrentUser: (accessToken: string) => async (dispatch: AppDispatch) => {
        try {
            dispatch(AuthSlice.actions.setIsLoading(true));

            // SignInSettings — public endpoint, надо вызывать всегда: фронт по нему узнаёт
            // фактический authMode сервера и обновляет кэш, чтобы LoginForm корректно отправил
            // следующий /SignIn. UserService.getCurrentUser нужен только если есть access (или
            // bootstrap silent refresh положил его в память) — иначе запрос даст 401.
            const settingsPromise = AuthService.fetchSignInSettings();
            const userPromise = accessToken
                ? UserService.getCurrentUser()
                : Promise.resolve(null);
            // allSettled, не all: 401 профиля (протухший access) не должен ронять обработку
            // настроек — иначе authMode-кэш не синхронизируется и LoginForm шлёт /SignIn
            // в устаревшем режиме, пока пользователь не очистит storage.
            const [userResult, settingsResult] = await Promise.allSettled([userPromise, settingsPromise]);

            if (settingsResult.status === 'fulfilled' && settingsResult.value) {
                applySignInSettings(dispatch, settingsResult.value.data);
            }

            // Таймер ротации для токена из storage/памяти уже поставил startAuth.
            const userResponse = userResult.status === 'fulfilled' ? userResult.value : null;
            if (userResponse) dispatch(AuthSlice.actions.successLogin({ user: userResponse.data }));
        } catch (e) {
            console.log(e);
        } finally {
            dispatch(AuthSlice.actions.setIsLoading(false));
        }
    },

    setIsLoading: (flag: boolean) => async (dispatch: AppDispatch) => {
        dispatch(AuthSlice.actions.setIsLoading(flag));
    },

    setIsLoggingIn: (flag: boolean) => async (dispatch: AppDispatch) => {
        dispatch(AuthSlice.actions.setIsLoggingIn(flag));
    },

    loginTwoFactor:
        (service: TwoFactorAuthenticationService, username: string, password: string, login: () => void) =>
        (dispatch: AppDispatch) => {
            dispatch(AuthSlice.actions.setError({ message: '', login: username, loginType: 'LoginPassTwoFactor' }));
            dispatch(AuthSlice.actions.setIsLoggingIn(true));
            dispatch(AuthSlice.actions.setIsSmsOtp(false));

            service.login(
                username,
                password,
                () => {
                    login();
                },
                (msg) => {
                    console.log(msg);
                    dispatch(
                        AuthSlice.actions.setError({ message: msg, login: username, loginType: 'LoginPassTwoFactor' }),
                    );
                    dispatch(AuthSlice.actions.setIsLoggingIn(false));
                },
                () => {
                    dispatch(AuthSlice.actions.setIsSmsOtp(true));
                },
            );
        },
};
